import { Badge } from '@mogzo/ui';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { ApiError, apiSend, type Project } from '../lib/api';
import { timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';
import { Alert, Button, cardClass, ErrorNotice, Loading } from './ui';

type Env = 'test' | 'prod';
type Status = 'open' | 'ended' | 'length' | 'empty' | 'closed';

interface RoomPerson {
  playerId: string;
  /** Null in live, where players are anonymous ids. */
  name: string | null;
  host: boolean;
  left: boolean;
}
interface ListedRoom {
  id: string;
  mode: string;
  modeName: string;
  players: RoomPerson[];
  openedAt: string;
  closedAt: string | null;
  seconds: number;
  status: Status;
}
interface RoomList {
  summary: {
    roomsNow: number;
    playersNow: number;
    roomHoursThisWeek: number;
    dropped: number;
    cameBack: number;
  };
  hasMore: boolean;
  rooms: ListedRoom[];
}
interface RoomDetail extends ListedRoom {
  state: unknown;
  messages: { perSecond: number; averageBytes: number; dropped: number };
  events: {
    at: string;
    kind: string;
    player: { playerId: string; name: string | null } | null;
    detail: string | null;
  }[];
}

const who = (person: { playerId: string; name: string | null }) =>
  person.name ?? `Player ${person.playerId.slice(0, 10)}`;

const clock = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const STATUS: Record<Status, [string, 'success' | 'neutral']> = {
  open: ['Playing', 'success'],
  ended: ['Ended', 'neutral'],
  length: ['Ran its length', 'neutral'],
  empty: ['Closed when empty', 'neutral'],
  closed: ['Closed by you', 'neutral'],
};

function StatusBadge({ status }: { status: Status }) {
  const [label, tone] = STATUS[status];
  return (
    <Badge tone={tone} dot={status === 'open'}>
      {label}
    </Badge>
  );
}

/** One line of a room's timeline, in plain words. */
function eventText(event: RoomDetail['events'][number]) {
  const name = event.player ? who(event.player) : '';
  switch (event.kind) {
    case 'opened':
      return 'The room opened';
    case 'joined':
      return `${name} came in`;
    case 'away':
      return `${name} dropped. Their place was kept`;
    case 'back':
      return `${name} came back, after ${event.detail} seconds`;
    case 'left':
      return `${name} left`;
    case 'host':
      return `${name} became the host`;
    case 'state':
      return `${name} set the room state`;
    case 'too_fast':
      return `${name} went over ${event.detail} messages a second; extra ones were dropped, told once`;
    case 'too_big':
      return `${name} sent a message over ${event.detail} bytes; it was dropped, told once`;
    case 'closed':
      return `The room closed (${STATUS[(event.detail as Status) ?? 'closed']?.[0].toLowerCase() ?? event.detail})`;
    default:
      return event.kind;
  }
}

/** One room: who was in it, what happened, its state now and its messages. */
function RoomPage({
  project,
  env,
  id,
  onBack,
}: {
  project: Project;
  env: Env;
  id: string;
  onBack: () => void;
}) {
  const [reload, setReload] = useState(0);
  const room = useApi<RoomDetail>(
    `/v1/dev/projects/${project.id}/rooms/${id}?env=${env}&r=${reload}`,
  );
  const [closing, setClosing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function close() {
    setClosing(true);
    setProblem(null);
    try {
      await apiSend('POST', `/v1/dev/projects/${project.id}/rooms/${id}/close`);
      // The room server tells MOGZO it closed a moment later.
      setTimeout(() => setReload((n) => n + 1), 1500);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'Couldn’t close it. Try again.');
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="secondary" onClick={onBack}>
          <ChevronLeft size={16} aria-hidden="true" />
          Rooms
        </Button>
      </div>
      {room.status === 'loading' && <Loading />}
      {room.status === 'error' && <ErrorNotice error={room.error} />}
      {room.status === 'ready' && (
        <section aria-labelledby="room-heading" className={`${cardClass} flex flex-col gap-4 p-5`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2
                id="room-heading"
                className="flex flex-wrap items-center gap-2.5 text-lg font-semibold"
              >
                Room <span className="font-mono font-medium">{room.data.id.slice(0, 8)}</span> ·{' '}
                {room.data.modeName}
                <StatusBadge status={room.data.status} />
              </h2>
              <p className="mt-0.5 text-[13px] text-muted">
                Opened {timeAgo(room.data.openedAt)} · {clock(room.data.seconds)} long ·{' '}
                {room.data.players.map(who).join(', ')}
              </p>
            </div>
            {env === 'test' && room.data.status === 'open' && (
              <Button variant="secondary" onClick={close} disabled={closing}>
                {closing ? 'Closing…' : 'Close room'}
              </Button>
            )}
          </div>
          {problem && <Alert tone="error">{problem}</Alert>}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6">
            <div>
              <h3 className="mb-1.5 text-[13px] font-semibold text-muted">What happened</h3>
              {room.data.events.length === 0 ? (
                <p className="text-sm text-muted">
                  Nothing yet. The room reports every few seconds.
                </p>
              ) : (
                <ol className="m-0 list-none p-0">
                  {room.data.events.map((event, i) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: events have no id of their own
                      key={i}
                      className="flex gap-3 border-b border-divider py-2 text-sm"
                    >
                      <span className="w-[52px] flex-none font-mono text-muted tabular-nums">
                        {clock(
                          Math.max(
                            0,
                            Math.round(
                              (new Date(event.at).getTime() -
                                new Date(room.data.openedAt).getTime()) /
                                1000,
                            ),
                          ),
                        )}
                      </span>
                      <span>{eventText(event)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div>
              <h3 className="mb-1.5 text-[13px] font-semibold text-muted">Room state, now</h3>
              <pre className="max-h-[360px] overflow-auto rounded-[6px] bg-field px-3 py-2.5 font-mono text-xs leading-[19px]">
                {JSON.stringify(room.data.state, null, 2)}
              </pre>
              <h3 className="mt-4 mb-1.5 text-[13px] font-semibold text-muted">Messages</h3>
              <p className="text-sm">
                {room.data.messages.perSecond.toLocaleString('en')} a second on average ·{' '}
                {room.data.messages.averageBytes.toLocaleString('en')} bytes on average ·{' '}
                {room.data.messages.dropped.toLocaleString('en')} dropped
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * The data browser's Rooms page (M35): realtime rooms, open ones first, with who came and
 * went. Messages pass through rooms and aren't kept.
 */
export function Rooms({ project, env }: { project: Project; env: Env }) {
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const list = useApi<RoomList>(`/v1/dev/projects/${project.id}/rooms?env=${env}&offset=${offset}`);

  if (open) return <RoomPage project={project} env={env} id={open} onBack={() => setOpen(null)} />;

  const summary = list.status === 'ready' ? list.data.summary : null;
  const cell = 'border-b border-divider px-3.5 py-3';
  return (
    <div className="flex flex-col gap-4">
      {summary && (
        <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
          {(
            [
              ['Rooms now', summary.roomsNow.toLocaleString('en')],
              ['Players in rooms', summary.playersNow.toLocaleString('en')],
              ['Room-hours this week', summary.roomHoursThisWeek.toLocaleString('en')],
              [
                'Came back after dropping',
                summary.dropped ? `${summary.cameBack} of ${summary.dropped}` : '–',
              ],
            ] as const
          ).map(([label, value]) => (
            <div key={label} className="mz-panel px-4 py-3">
              <dt className="text-[13px] text-muted">{label}</dt>
              <dd className="mz-text-title-lg m-0 mt-1 tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div>
        <h2 className="text-[15px] font-semibold">Rooms</h2>
        <p className="mt-0.5 text-[13px] text-muted">
          Rooms in {env === 'test' ? 'test' : 'live'}, open ones first. MOGZO keeps who came and
          went, not the messages: those only pass through.
        </p>
      </div>

      {list.status === 'loading' && <Loading />}
      {list.status === 'error' && <ErrorNotice error={list.error} />}
      {list.status === 'ready' &&
        (list.data.rooms.length === 0 ? (
          <p className={`${cardClass} px-5 py-6 text-sm text-muted`}>
            {env === 'test'
              ? 'No rooms yet. They show here as soon as testers play a realtime mode.'
              : 'No live rooms yet.'}
          </p>
        ) : (
          <div className={`${cardClass} overflow-x-auto`}>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="border-b border-divider px-3.5 py-2.5">Mode</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Players</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Opened</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Length</th>
                  <th className="border-b border-divider px-3.5 py-2.5">Status</th>
                  <th className="border-b border-divider px-3.5 py-2.5">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.rooms.map((room) => (
                  <tr key={room.id}>
                    <td className={`${cell} font-semibold`}>{room.modeName}</td>
                    <td className={cell}>
                      <span className="flex flex-wrap gap-x-3 gap-y-1">
                        {room.players.map((p) => (
                          <span
                            key={p.playerId}
                            className={p.left ? 'text-muted line-through' : undefined}
                          >
                            {who(p)}
                            {p.host && <span className="ml-1 text-xs text-muted">host</span>}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className={`${cell} text-muted`}>{timeAgo(room.openedAt)}</td>
                    <td className={`${cell} tabular-nums`}>{clock(room.seconds)}</td>
                    <td className={cell}>
                      <StatusBadge status={room.status} />
                    </td>
                    <td className="border-b border-divider px-3.5 py-2 text-right">
                      <Button
                        variant="secondary"
                        onClick={() => setOpen(room.id)}
                        aria-label={`Open the ${room.modeName} room from ${timeAgo(room.openedAt)}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {list.status === 'ready' && (offset > 0 || list.data.hasMore) && (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
          >
            Newer
          </Button>
          <Button
            variant="secondary"
            disabled={!list.data.hasMore}
            onClick={() => setOffset(offset + 50)}
          >
            Older
          </Button>
        </div>
      )}
      <p className="text-[13px] text-muted">
        {env === 'test'
          ? 'You can close an open test room. In live, players show as anonymous ids.'
          : 'Players show as anonymous ids.'}
      </p>
    </div>
  );
}
